import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/Button";
import { useHubToast } from "@/components/ui/HubToast";
import { ApiError, apiFetch } from "@/lib/api";
import { useManufacturingScopeHubWrite } from "@/lib/useHubModuleWrite";

import { hubNativeReceiveBlockedReason } from "./hubNativeReceiveBlockedReason";
import { RebuildBomDialog } from "./WorkOrderRebuildBomDialog";
import {
  WorkOrderDeviationDialog,
  type WorkOrderDeviationRecord,
} from "./WorkOrderDeviationDialog";
import type { WorkOrderDeviationApplyPayload } from "./workOrderDeviationUtils";
import {
  AllocateMaterialsModal,
  type PartialMaterialAllocation,
  ReceiveFinishedGoodModal,
  ReturnMaterialsModal,
  WorkOrderCancelModal,
} from "./workOrderDetailModals";
import { WorkOrderDetailPanel } from "./workOrderDetailPanel";
import {
  type RebuildRequestSummary,
  type RebuildResponse,
  type WorkOrderDetail,
  isHubNativeWorkOrder,
} from "./workOrdersPageModel";

type AllocatePayload = { id: number; allocations: PartialMaterialAllocation[] };

type HubWorkOrderReceiptRow = {
  id: number | string;
  receive_quantity: number;
  status: string;
  mv_work_order_id?: number | string | null;
  mv_work_order_no?: string | null;
  reverse_document_date?: string | null;
  mv_adj_minus_no?: string | null;
  mv_adj_plus_no?: string | null;
  qbo_labor_je_id?: string | null;
  qbo_labor_je_doc_number?: string | null;
  reversal_id?: number | string | null;
  audit_support_ticket_id?: number | string | null;
  labor_je_sync_status?: string | null;
  ops_support_ticket_id?: number | string | null;
  error_detail?: string | null;
  posted_at?: string | null;
};

export type WorkOrderDetailWorkspaceProps = {
  buildOrderId: number | null;
  onClose?: () => void;
  /** Called after a successful write so parent lists can refresh (for example paperwork WOFG flags). */
  onMutated?: () => void;
  /** Called when async detail finishes loading so anchored callouts can remeasure height. */
  onDetailReady?: () => void;
  /** CM PO receiving is the allowed place to post finished goods for a linked work order. */
  receiveFromCmPo?: boolean;
  /**
   * Anchored callouts already dismiss on outside click and Escape.
   * Those hosts omit the header X so there is only one close method.
   */
  showHeaderClose?: boolean;
};

export function WorkOrderDetailWorkspace({
  buildOrderId,
  onClose,
  onMutated,
  onDetailReady,
  receiveFromCmPo = false,
  showHeaderClose = true,
}: WorkOrderDetailWorkspaceProps): JSX.Element | null {
  const queryClient = useQueryClient();
  const { showToast } = useHubToast();
  const hubWritesAllowed = useManufacturingScopeHubWrite();
  const [allocateModalOpen, setAllocateModalOpen] = useState(false);
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [rebuildOpen, setRebuildOpen] = useState(false);
  const [deviationOpen, setDeviationOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);

  const detailQuery = useQuery<WorkOrderDetail, ApiError>({
    queryKey: workOrderDetailQueryKey(buildOrderId ?? 0),
    queryFn: () => fetchWorkOrderDetail(buildOrderId ?? 0),
    enabled: buildOrderId != null,
    staleTime: WORK_ORDER_DETAIL_STALE_MS,
    placeholderData: (previousData) => {
      if (previousData != null && previousData.id === buildOrderId) {
        return previousData;
      }
      if (buildOrderId == null) {
        return undefined;
      }
      const cached = queryClient.getQueryData<WorkOrderDetail>(workOrderDetailQueryKey(buildOrderId));
      if (cached != null && cached.id === buildOrderId) {
        return cached;
      }
      return undefined;
    },
  });

  useEffect(() => {
    setAllocateModalOpen(false);
    setReceiveModalOpen(false);
    setReturnModalOpen(false);
    setRebuildOpen(false);
    setDeviationOpen(false);
    setCancelModalOpen(false);
  }, [buildOrderId]);

  useEffect(() => {
    if (buildOrderId != null && detailQuery.data != null && !detailQuery.isLoading) {
      onDetailReady?.();
    }
  }, [buildOrderId, detailQuery.data, detailQuery.isLoading, onDetailReady]);

  function refreshAll(): void {
    void queryClient.invalidateQueries({ queryKey: ["manufacturing", "work-orders"] });
    onMutated?.();
  }

  const saveHeaderMutation = useMutation<
    WorkOrderDetail,
    ApiError,
    { id: number; orderedQuantity: number; manufacturerReferenceNumber: string; notes: string }
  >({
    mutationFn: (payload) =>
      apiFetch<WorkOrderDetail>(`/api/manufacturing/work-orders/${payload.id}`, {
        method: "PATCH",
        body: {
          orderedQuantity: payload.orderedQuantity,
          manufacturerReferenceNumber: payload.manufacturerReferenceNumber,
          notes: payload.notes,
        },
      }),
    onSuccess: () => {
      refreshAll();
    },
  });

  const allocateMutation = useMutation<WorkOrderDetail, ApiError, AllocatePayload>({
    mutationFn: (payload) =>
      apiFetch<WorkOrderDetail>(`/api/manufacturing/work-orders/${payload.id}/allocate`, {
        method: "POST",
        body: { allocations: payload.allocations },
      }),
    onSuccess: () => {
      setAllocateModalOpen(false);
      refreshAll();
    },
  });

  const receiveCloseMutation = useMutation<
    WorkOrderDetail,
    ApiError,
    { id: number; quantity: number; close: boolean }
  >({
    mutationFn: async ({ id, quantity, close }) => {
      const detail = queryClient.getQueryData<WorkOrderDetail>(workOrderDetailQueryKey(id));
      if (detail != null && isHubNativeWorkOrder(detail)) {
        const result = await apiFetch<{ workOrder: WorkOrderDetail }>(
          `/api/manufacturing/work-orders/${id}/receipts`,
          {
            method: "POST",
            body: {
              receiveQuantity: quantity,
              closeAfter: close,
              fromCmPoReceiving: receiveFromCmPo,
            },
          },
        );
        return result.workOrder;
      }
      return apiFetch<WorkOrderDetail>(`/api/manufacturing/work-orders/${id}/receive-close`, {
        method: "POST",
        body: { quantity, close, fromCmPoReceiving: receiveFromCmPo },
      });
    },
    onSuccess: () => {
      setReceiveModalOpen(false);
      refreshAll();
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "work-orders", "receipts", buildOrderId] });
    },
    onError: (error) => {
      showToast({ tone: "error", message: error.message });
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "work-orders", "receipts", buildOrderId] });
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "work-orders", "receive-cap", buildOrderId] });
    },
  });

  const receiptsQuery = useQuery<{ receipts: HubWorkOrderReceiptRow[] }, ApiError>({
    queryKey: ["manufacturing", "work-orders", "receipts", buildOrderId],
    queryFn: () =>
      apiFetch<{ receipts: HubWorkOrderReceiptRow[] }>(
        `/api/manufacturing/work-orders/${buildOrderId ?? 0}/receipts`,
      ),
    enabled: buildOrderId != null && detailQuery.data != null && isHubNativeWorkOrder(detailQuery.data),
  });

  const reverseReceiptMutation = useMutation<
    unknown,
    ApiError,
    { receiptId: number }
  >({
    mutationFn: ({ receiptId }) =>
      apiFetch(`/api/manufacturing/work-orders/${buildOrderId}/receipts/${receiptId}/reverse`, {
        method: "POST",
        body: {},
      }),
    onSuccess: () => {
      refreshAll();
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "work-orders", "receipts", buildOrderId] });
      void queryClient.invalidateQueries({ queryKey: workOrderDetailQueryKey(buildOrderId ?? 0) });
    },
  });

  const returnMaterialsMutation = useMutation<
    WorkOrderDetail,
    ApiError,
    { id: number; returns: PartialMaterialAllocation[] }
  >({
    mutationFn: ({ id, returns }) =>
      apiFetch<WorkOrderDetail>(`/api/manufacturing/work-orders/${id}/return-materials`, {
        method: "POST",
        body: { returns },
      }),
    onSuccess: () => {
      setReturnModalOpen(false);
      refreshAll();
    },
  });

  const syncInternalLaborMutation = useMutation<WorkOrderDetail, ApiError, number>({
    mutationFn: (id) =>
      apiFetch<WorkOrderDetail>(`/api/manufacturing/work-orders/${id}/sync-internal-labor-overhead`, {
        method: "POST",
      }),
    onSuccess: () => {
      refreshAll();
    },
  });

  const patchExternalLabourMutation = useMutation<
    WorkOrderDetail,
    ApiError,
    { id: number; labourCost: number; chargeId?: number }
  >({
    mutationFn: ({ id, labourCost, chargeId }) =>
      apiFetch<WorkOrderDetail>(`/api/manufacturing/work-orders/${id}/external-labour-cost`, {
        method: "PATCH",
        body: {
          labour_cost: labourCost,
          ...(chargeId != null ? { charge_id: chargeId } : {}),
        },
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(workOrderDetailQueryKey(buildOrderId ?? 0), result);
      refreshAll();
    },
  });

  const addAdditionalChargeMutation = useMutation<
    WorkOrderDetail,
    ApiError,
    { id: number; cmPoId: number; labourCost: number }
  >({
    mutationFn: ({ id, cmPoId, labourCost }) =>
      apiFetch<WorkOrderDetail>(`/api/manufacturing/work-orders/${id}/external-labour-charges`, {
        method: "POST",
        body: { cm_po_id: cmPoId, labour_cost: labourCost },
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(workOrderDetailQueryKey(buildOrderId ?? 0), result);
      refreshAll();
    },
  });

  const rebuildRequestsQuery = useQuery<{ requests: RebuildRequestSummary[] }, ApiError>({
    queryKey: ["manufacturing", "work-order-rebuild-requests", "for-build-order-mcp", buildOrderId],
    queryFn: () =>
      apiFetch<{ requests: RebuildRequestSummary[] }>("/api/manufacturing/work-order-rebuild-requests", {
        query: { buildOrderId: String(buildOrderId), status: "pending", source: "mcp" },
      }),
    enabled: buildOrderId != null,
    staleTime: 10_000,
  });

  const deviationsQuery = useQuery<{ deviations: WorkOrderDeviationRecord[] }, ApiError>({
    queryKey: ["manufacturing", "work-orders", "deviations", buildOrderId],
    queryFn: () =>
      apiFetch<{ deviations: WorkOrderDeviationRecord[] }>(
        `/api/manufacturing/work-orders/${buildOrderId ?? 0}/deviations`,
      ),
    enabled: buildOrderId != null && detailQuery.data != null && isHubNativeWorkOrder(detailQuery.data),
  });

  const applyDeviationMutation = useMutation<
    { workOrder: WorkOrderDetail; deviationId: number },
    ApiError,
    { id: number; payload: WorkOrderDeviationApplyPayload }
  >({
    mutationFn: ({ id, payload }) =>
      apiFetch<{ workOrder: WorkOrderDetail; deviationId: number }>(
        `/api/manufacturing/work-orders/${id}/deviations`,
        {
          method: "POST",
          body: payload,
        },
      ),
    onSuccess: (result) => {
      setDeviationOpen(false);
      queryClient.setQueryData(workOrderDetailQueryKey(buildOrderId ?? 0), result.workOrder);
      refreshAll();
      void queryClient.invalidateQueries({
        queryKey: ["manufacturing", "work-orders", "deviations", buildOrderId],
      });
    },
  });

  const cancelMutation = useMutation<WorkOrderDetail, ApiError, { id: number; reason: string }>({
    mutationFn: ({ id, reason }) =>
      apiFetch<WorkOrderDetail>(`/api/manufacturing/work-orders/${id}/cancel`, {
        method: "POST",
        body: { reason },
      }),
    onSuccess: (result) => {
      setCancelModalOpen(false);
      queryClient.setQueryData(workOrderDetailQueryKey(buildOrderId ?? 0), result);
      refreshAll();
    },
  });

  const rebuildBomMutation = useMutation<
    RebuildResponse,
    ApiError,
    {
      id: number;
      lines: Array<{
        productSku: string;
        rawMaterialQuantity: number;
        replacementSku: string | null;
        replacementQuantity: number | null;
      }>;
    }
  >({
    mutationFn: ({ id, lines }) =>
      apiFetch(`/api/manufacturing/work-orders/${id}/rebuild-bom`, {
        method: "POST",
        body: { lines },
      }),
    onSuccess: () => {
      setRebuildOpen(false);
      refreshAll();
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "work-order-rebuild-requests"] });
    },
  });

  const approveRebuildMutation = useMutation<RebuildResponse, ApiError, { requestId: number }>({
    mutationFn: ({ requestId }) =>
      apiFetch(`/api/manufacturing/work-order-rebuild-requests/${requestId}/approve`, {
        method: "POST",
        body: {},
      }),
    onSuccess: () => {
      refreshAll();
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "work-order-rebuild-requests"] });
    },
  });

  const rejectRebuildMutation = useMutation<{ ok: true; id: number }, ApiError, { requestId: number }>({
    mutationFn: ({ requestId }) =>
      apiFetch(`/api/manufacturing/work-order-rebuild-requests/${requestId}/reject`, {
        method: "POST",
        body: {},
      }),
    onSuccess: () => {
      refreshAll();
      void queryClient.invalidateQueries({ queryKey: ["manufacturing", "work-order-rebuild-requests"] });
    },
  });

  const pendingDeviationCount = useMemo(() => {
    return (deviationsQuery.data?.deviations ?? []).filter(
      (deviation) => deviation.status === "pending_allocation_resolution",
    ).length;
  }, [deviationsQuery.data]);

  const pendingRebuild = useMemo(() => {
    const requests = rebuildRequestsQuery.data?.requests;
    if (buildOrderId == null || !requests) {
      return null;
    }
    return requests.find((requestRow) => requestRow.build_order_id === buildOrderId) ?? null;
  }, [buildOrderId, rebuildRequestsQuery.data]);

  const hubNativeReceiveBlockedReasonText = useMemo(
    () => hubNativeReceiveBlockedReason(receiptsQuery.data?.receipts ?? []),
    [receiptsQuery.data?.receipts],
  );

  if (buildOrderId == null) {
    return null;
  }

  const rawDetail = detailQuery.data ?? null;
  const detail = rawDetail != null && rawDetail.id === buildOrderId ? rawDetail : null;
  const isDetailLoading =
    detailQuery.isLoading || (detailQuery.isFetching && detail == null);

  return (
    <>
      <WorkOrderDetailPanel
        detail={detail}
        isLoading={isDetailLoading}
        error={detailQuery.error ?? null}
        onClose={showHeaderClose && onClose != null ? () => onClose() : undefined}
        onOpenAllocateModal={() => setAllocateModalOpen(true)}
        allocateState={allocateMutation}
        onOpenReceiveModal={() => setReceiveModalOpen(true)}
        hubNativeReceiveBlockedReason={hubNativeReceiveBlockedReasonText}
        receiveFromCmPo={receiveFromCmPo}
        onOpenReturnModal={() => setReturnModalOpen(true)}
        receiveCloseState={receiveCloseMutation}
        returnMaterialsState={returnMaterialsMutation}
        syncInternalLaborState={syncInternalLaborMutation}
        patchExternalLabourState={patchExternalLabourMutation}
        addAdditionalChargeState={addAdditionalChargeMutation}
        onRebuildOpen={() => setRebuildOpen(true)}
        onDeviationOpen={() => setDeviationOpen(true)}
        onOpenCancelModal={() => setCancelModalOpen(true)}
        onSaveHeader={(payload) => saveHeaderMutation.mutate(payload)}
        saveHeaderState={saveHeaderMutation}
        cancelState={cancelMutation}
        pendingDeviationCount={pendingDeviationCount}
        hubWritesAllowed={hubWritesAllowed}
        pendingRebuild={pendingRebuild}
        rebuildRequestsLoading={rebuildRequestsQuery.isLoading}
        onApproveRebuild={(requestId) => approveRebuildMutation.mutate({ requestId })}
        onRejectRebuild={(requestId) => rejectRebuildMutation.mutate({ requestId })}
        approveRebuildPending={approveRebuildMutation.isPending}
        rejectRebuildPending={rejectRebuildMutation.isPending}
        approveRebuildError={approveRebuildMutation.error ?? null}
        rejectRebuildError={rejectRebuildMutation.error ?? null}
      />

      {detail != null && isHubNativeWorkOrder(detail) ? (
        <div className="mt-3 rounded-md border border-slate-200 bg-white p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Hub receipts</div>
          {receiptsQuery.isLoading ? (
            <p className="mt-2 text-sm text-slate-500">Loading receipts…</p>
          ) : null}
          {receiptsQuery.error ? (
            <p className="mt-2 text-sm text-rose-700">{receiptsQuery.error.message}</p>
          ) : null}
          {(receiptsQuery.data?.receipts ?? []).length === 0 && !receiptsQuery.isLoading ? (
            <p className="mt-2 text-sm text-slate-600">No hub receipts yet.</p>
          ) : null}
          <ul className="mt-2 space-y-2">
            {(receiptsQuery.data?.receipts ?? []).map((receipt) => {
              const receiptId = Number(receipt.id);
              const canReverse = receipt.status === "posted" && hubWritesAllowed;
              return (
                <li
                  key={String(receipt.id)}
                  className="flex flex-wrap items-center justify-between gap-2 rounded border border-slate-100 bg-slate-50 px-2 py-2 text-sm"
                >
                  <div className="min-w-0">
                    <div className="font-mono text-slate-900">
                      #{receiptId} · qty {receipt.receive_quantity} · {receipt.status}
                    </div>
                    <div className="text-xs text-slate-500">
                      {receipt.mv_work_order_no ? `MV ${receipt.mv_work_order_no}` : "MV pending"}
                      {receipt.status === "reversed" && receipt.reverse_document_date
                        ? ` · reversed ${receipt.reverse_document_date}`
                        : null}
                      {receipt.mv_adj_minus_no || receipt.mv_adj_plus_no
                        ? ` · adj− ${receipt.mv_adj_minus_no ?? "—"} / adj+ ${receipt.mv_adj_plus_no ?? "—"}`
                        : null}
                      {receipt.qbo_labor_je_doc_number
                        ? ` · JE ${receipt.qbo_labor_je_doc_number}`
                        : receipt.labor_je_sync_status === "pending" ||
                            receipt.labor_je_sync_status === "posting" ||
                            receipt.labor_je_sync_status === "failed"
                          ? ` · JE ${receipt.labor_je_sync_status} (queued)`
                          : receipt.status === "reversed"
                            ? " · JE none (no labor liability)"
                            : null}
                      {receipt.reversal_id != null ? ` · reversal #${String(receipt.reversal_id)}` : null}
                      {receipt.audit_support_ticket_id != null
                        ? ` · audit ticket #${String(receipt.audit_support_ticket_id)}`
                        : null}
                      {receipt.ops_support_ticket_id != null
                        ? ` · ops ticket #${String(receipt.ops_support_ticket_id)}`
                        : null}
                      {receipt.status === "failed" && receipt.error_detail
                        ? ` · ${receipt.error_detail}`
                        : null}
                    </div>
                  </div>
                  {canReverse ? (
                    <Button
                      variant="secondary"
                      size="sm"
                      isLoading={reverseReceiptMutation.isPending}
                      disabled={reverseReceiptMutation.isPending}
                      onClick={() => {
                        if (
                          !window.confirm(
                            `Reverse receipt #${receiptId}? This posts adj− finished good and adj+ components dated today, then a QBO journal entry moving labor from Inventory Shrinkage back to Work Order liability accounts.`,
                          )
                        ) {
                          return;
                        }
                        reverseReceiptMutation.mutate({ receiptId });
                      }}
                    >
                      Reverse receipt
                    </Button>
                  ) : null}
                </li>
              );
            })}
          </ul>
          {reverseReceiptMutation.error ? (
            <p className="mt-2 text-sm text-rose-700">{reverseReceiptMutation.error.message}</p>
          ) : null}
        </div>
      ) : null}

      {allocateModalOpen && detail ? (
        <AllocateMaterialsModal
          workOrder={detail}
          onClose={() => {
            if (allocateMutation.isPending) return;
            allocateMutation.reset();
            setAllocateModalOpen(false);
          }}
          onAllocatePartial={(allocations) =>
            allocateMutation.mutate({ id: buildOrderId, allocations })
          }
          isPending={allocateMutation.isPending}
          error={allocateMutation.error ?? null}
          hubWritesAllowed={hubWritesAllowed}
        />
      ) : null}

      {receiveModalOpen && detail ? (
        <ReceiveFinishedGoodModal
          workOrder={detail}
          buildOrderId={buildOrderId}
          onClose={() => {
            if (receiveCloseMutation.isPending) return;
            receiveCloseMutation.reset();
            setReceiveModalOpen(false);
          }}
          onReceive={(quantity, close) =>
            receiveCloseMutation.mutate({ id: buildOrderId, quantity, close })
          }
          isPending={receiveCloseMutation.isPending}
        />
      ) : null}

      {returnModalOpen && detail ? (
        <ReturnMaterialsModal
          workOrder={detail}
          buildOrderId={buildOrderId}
          onClose={() => {
            if (returnMaterialsMutation.isPending) return;
            returnMaterialsMutation.reset();
            setReturnModalOpen(false);
          }}
          onReturn={(returns) => returnMaterialsMutation.mutate({ id: buildOrderId, returns })}
          isPending={returnMaterialsMutation.isPending}
          error={returnMaterialsMutation.error ?? null}
        />
      ) : null}

      {rebuildOpen && detail && !isHubNativeWorkOrder(detail) ? (
        <RebuildBomDialog
          workOrder={detail}
          hubWritesAllowed={hubWritesAllowed}
          onClose={() => {
            if (rebuildBomMutation.isPending) return;
            setRebuildOpen(false);
          }}
          onConfirm={(lines) => rebuildBomMutation.mutate({ id: detail.id, lines })}
          isSubmitting={rebuildBomMutation.isPending}
          error={rebuildBomMutation.error ?? null}
          successMessage={
            rebuildBomMutation.data
              ? `Rebuild finished. New Megaventory work order ${rebuildBomMutation.data.newMvNumber} (id ${rebuildBomMutation.data.newMvId}). ${rebuildBomMutation.data.message}`
              : null
          }
        />
      ) : null}

      {deviationOpen && detail && isHubNativeWorkOrder(detail) ? (
        <WorkOrderDeviationDialog
          workOrder={detail}
          deviations={deviationsQuery.data?.deviations ?? []}
          hubWritesAllowed={hubWritesAllowed}
          onClose={() => {
            if (applyDeviationMutation.isPending) return;
            applyDeviationMutation.reset();
            setDeviationOpen(false);
          }}
          onConfirm={(payload) => applyDeviationMutation.mutate({ id: detail.id, payload })}
          isSubmitting={applyDeviationMutation.isPending}
          error={applyDeviationMutation.error ?? null}
          successMessage={
            applyDeviationMutation.data
              ? `Deviation #${applyDeviationMutation.data.deviationId} applied.`
              : null
          }
        />
      ) : null}

      {cancelModalOpen && detail ? (
        <WorkOrderCancelModal
          open={cancelModalOpen}
          detail={detail}
          hubWritesAllowed={hubWritesAllowed}
          isPending={cancelMutation.isPending}
          error={cancelMutation.error ?? null}
          onClose={() => {
            if (cancelMutation.isPending) return;
            cancelMutation.reset();
            setCancelModalOpen(false);
          }}
          onConfirm={(reason) => cancelMutation.mutate({ id: detail.id, reason })}
        />
      ) : null}
    </>
  );
}

export const PAPERWORK_PROCESS_WO_ANCHOR_ATTR = "data-paperwork-process-wo";

export function workOrderDetailQueryKey(buildOrderId: number): readonly [string, string, string, number] {
  return ["manufacturing", "work-orders", "detail", buildOrderId];
}

export function fetchWorkOrderDetail(buildOrderId: number): Promise<WorkOrderDetail> {
  return apiFetch<WorkOrderDetail>(`/api/manufacturing/work-orders/${buildOrderId}`);
}

export const WORK_ORDER_DETAIL_STALE_MS = 60_000;

export function prefetchWorkOrderDetail(queryClient: QueryClient, buildOrderId: number): Promise<void> {
  return queryClient
    .ensureQueryData({
      queryKey: workOrderDetailQueryKey(buildOrderId),
      queryFn: () => fetchWorkOrderDetail(buildOrderId),
      staleTime: WORK_ORDER_DETAIL_STALE_MS,
    })
    .then(() => undefined);
}

/** Prefetch work-order detail for every Process WO row under an expanded paperwork shipment. */
export async function prefetchWorkOrdersForPaperworkShipment(
  queryClient: QueryClient,
  shipment: Parameters<typeof buildOrderIdsFromPaperworkShipment>[0],
): Promise<void> {
  for (const buildOrderId of buildOrderIdsFromPaperworkShipment(shipment)) {
    await prefetchWorkOrderDetail(queryClient, buildOrderId);
  }
}

export function buildOrderIdsFromPaperworkShipment(shipment: {
  sales_orders?: readonly { batch_items?: readonly { build_order_id: number | null }[] }[];
}): number[] {
  const ids = new Set<number>();
  for (const salesOrder of shipment.sales_orders ?? []) {
    for (const item of salesOrder.batch_items ?? []) {
      if (item.build_order_id != null) {
        ids.add(item.build_order_id);
      }
    }
  }
  return [...ids];
}

export function paperworkProcessWoAnchorKey(buildOrderId: number): string {
  return String(buildOrderId);
}
